import { useState } from "react";
import type { FormEvent } from "react";
import { categories } from "@/mocks/categories";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dah8463p14a1h3mr6ev0";

export default function SubmitForm() {
  const [content, setContent] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "submitting") return;

    const formEl = event.currentTarget;
    const formData = new FormData(formEl);

    const extraValue = String(formData.get("website_alt") ?? "").trim();
    if (extraValue) {
      setStatus("success");
      setFormError("");
      formEl.reset();
      setContent("");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value);
      if (text.trim() === "") return;
      payload.append(key, text);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = parsed && typeof parsed.meta === "object" && parsed.meta !== null ? (parsed.meta as Record<string, unknown>) : null;
      const serverMsg = String(
        meta?.message ?? parsed?.message ?? meta?.detail ?? responseText ?? "",
      );
      const code = parsed ? String(parsed.code ?? "") : "";
      const looksLikeSpam = /spam|垃圾|spam/i.test(serverMsg);

      if (response.ok && code === "OK" && !looksLikeSpam) {
        setStatus("success");
        formEl.reset();
        setContent("");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交没有成功，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  if (status === "success") {
    return (
      <div className="flex flex-col items-center rounded-lg border border-accent-200 bg-accent-50 px-6 py-14 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-500 text-foreground-950">
          <i className="ri-checkbox-circle-line text-3xl" aria-hidden="true" />
        </span>
        <h2 className="mt-5 font-display text-xl text-foreground-950">投稿已收到，谢谢你</h2>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-700">
          编辑会在工作时段内整理你的线索，必要时会通过你留下的联系方式与你核实。若不需要核实，会直接安排发布。
        </p>
        <button
          type="button"
          onClick={() => setStatus("idle")}
          className="mt-6 inline-flex items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-add-line mr-1.5" aria-hidden="true" />
          再投一条
        </button>
      </div>
    );
  }

  return (
    <form
      id="baoliao-submit-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h2 className="font-display text-xl text-foreground-950">填写爆料内容</h2>
      <p className="mt-2 text-sm leading-relaxed text-foreground-600">
        带 <span className="text-primary-600">*</span> 的为必填项。写得越具体，越容易通过整理。
      </p>

      <div className="mt-6 flex flex-col gap-5">
        <div className="flex flex-col gap-2">
          <label htmlFor="submit-nickname" className="font-label text-sm text-foreground-800">
            你的化名
          </label>
          <input
            id="submit-nickname"
            name="nickname"
            type="text"
            maxLength={20}
            placeholder="例如：夜行的猫（可留空，默认匿名网友）"
            className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="submit-title" className="font-label text-sm text-foreground-800">
            爆料标题 <span className="text-primary-600">*</span>
          </label>
          <input
            id="submit-title"
            name="title"
            type="text"
            required
            maxLength={60}
            placeholder="一句话说清楚发生了什么，例如：我们小区的团购团长把账目贴在了电梯里"
            className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="submit-category" className="font-label text-sm text-foreground-800">
            爆料分类 <span className="text-primary-600">*</span>
          </label>
          <select
            id="submit-category"
            name="category"
            required
            defaultValue=""
            className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          >
            <option value="" disabled>
              请选择最贴近的分类
            </option>
            {categories.map((item) => (
              <option key={item.slug} value={item.label}>
                {item.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="submit-content" className="font-label text-sm text-foreground-800">
            爆料详情 <span className="text-primary-600">*</span>
          </label>
          <textarea
            id="submit-content"
            name="content"
            required
            rows={7}
            maxLength={500}
            value={content}
            onChange={(event) => setContent(event.target.value.slice(0, 500))}
            placeholder="请写清楚时间、地点和经过。不需要文采，把细节讲明白就好。"
            className="w-full resize-none rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
          />
          <p className="text-right font-label text-xs text-foreground-500">{content.length}/500</p>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="flex flex-col gap-2">
            <label htmlFor="submit-email" className="font-label text-sm text-foreground-800">
              联系邮箱（选填）
            </label>
            <input
              id="submit-email"
              name="email"
              type="email"
              placeholder="仅用于核实线索，不会公开"
              className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div className="flex flex-col gap-2">
            <label htmlFor="submit-contact" className="font-label text-sm text-foreground-800">
              其他联系方式（选填）
            </label>
            <input
              id="submit-contact"
              name="contact"
              type="text"
              maxLength={40}
              placeholder="微信号或手机号，同样不会公开"
              className="w-full rounded-md border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
          </div>
        </div>

        <div className="form-extra-slot">
          <label htmlFor="submit-website-alt">备用联系字段</label>
          <input
            id="submit-website-alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            readOnly
          />
        </div>
      </div>

      {status === "error" && formError ? (
        <p className="mt-5 flex items-start gap-2 rounded-md bg-primary-100 px-3.5 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
          <span>{formError}</span>
        </p>
      ) : null}

      <div className="mt-6 flex flex-col gap-3 border-t border-background-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="font-label text-xs leading-relaxed text-foreground-500">
          提交即表示你确认内容为亲身经历或亲眼所见，并同意我们在隐去个人信息后发布。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="inline-flex shrink-0 items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <i className="ri-send-plane-line mr-2" aria-hidden="true" />
          {status === "submitting" ? "正在提交…" : "提交爆料"}
        </button>
      </div>
    </form>
  );
}